import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import { logAuditEvent, AUDIT_ACTIONS, AUDIT_ENTITIES, getAuditLogs } from '../../services/auditLogService';

const ROLE_CONFIG = {
  admin: {
    label: 'Admin',
    icon: 'ShieldCheck',
    color: 'text-accent',
    bg: 'bg-accent/15',
    description: 'Full CRUD access including user management and financial settings',
  },
  estimator: {
    label: 'Estimator',
    icon: 'Briefcase',
    color: 'text-primary',
    bg: 'bg-primary/10',
    description: 'Manage projects and cost estimation, no user/financial access',
  },
  viewer: {
    label: 'Viewer',
    icon: 'Eye',
    color: 'text-muted-foreground',
    bg: 'bg-muted',
    description: 'Read-only access to dashboards and reports',
  },
};

const PERMISSIONS_MATRIX = [
  { feature: 'User Management (Add/Remove)', admin: true, estimator: false, viewer: false },
  { feature: 'Role Assignment', admin: true, estimator: false, viewer: false },
  { feature: 'Project Creation/Editing', admin: true, estimator: true, viewer: false },
  { feature: 'Cost Estimation & Material Lists', admin: true, estimator: true, viewer: false },
  { feature: 'Financial Settings/Markup', admin: true, estimator: false, viewer: false },
  { feature: 'View Dashboards & Reports', admin: true, estimator: true, viewer: true },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAdmin, profileLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [adminEmailInput, setAdminEmailInput] = useState('');
  const [addAdminLoading, setAddAdminLoading] = useState(false);
  const [addAdminError, setAddAdminError] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [userSearch, setUserSearch] = useState('');
  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditFilter, setAuditFilter] = useState('');

  useEffect(() => {
    if (profileLoading) return;
    if (!isAdmin) {
      navigate('/service-calculator', { replace: true });
      return;
    }
    fetchUsers();
    if (activeTab === 'audit') fetchAuditLogs();
  }, [isAdmin, profileLoading, activeTab]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setUsers(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    setAuditError('');
    try {
      const logs = await getAuditLogs({ limit: 200 });
      setAuditLogs(logs);
    } catch (err) {
      setAuditError(err?.message || 'Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  };

  const filteredAuditLogs = auditLogs?.filter(log =>
    !auditFilter ||
    log?.action?.toLowerCase()?.includes(auditFilter?.toLowerCase()) ||
    log?.user_email?.toLowerCase()?.includes(auditFilter?.toLowerCase()) ||
    log?.entity_type?.toLowerCase()?.includes(auditFilter?.toLowerCase()) ||
    log?.entity_name?.toLowerCase()?.includes(auditFilter?.toLowerCase())
  );

  const ACTION_LABELS = {
    user_role_changed: { label: 'Role Changed', color: 'text-warning', bg: 'bg-warning/10', icon: 'UserCog' },
    user_activated: { label: 'User Activated', color: 'text-success', bg: 'bg-success/10', icon: 'UserCheck' },
    user_deactivated: { label: 'User Deactivated', color: 'text-destructive', bg: 'bg-destructive/10', icon: 'UserX' },
    user_added_as_admin: { label: 'Admin Granted', color: 'text-accent', bg: 'bg-accent/10', icon: 'ShieldPlus' },
    permission_saved: { label: 'Permissions Saved', color: 'text-primary', bg: 'bg-primary/10', icon: 'ShieldCheck' },
    estimate_created: { label: 'Estimate Created', color: 'text-success', bg: 'bg-success/10', icon: 'FileText' },
    estimate_updated: { label: 'Estimate Updated', color: 'text-warning', bg: 'bg-warning/10', icon: 'FilePen' },
    estimate_deleted: { label: 'Estimate Deleted', color: 'text-destructive', bg: 'bg-destructive/10', icon: 'Trash2' },
    estimate_exported: { label: 'Estimate Exported', color: 'text-primary', bg: 'bg-primary/10', icon: 'Download' },
    award_issued: { label: 'Award Issued', color: 'text-accent', bg: 'bg-accent/10', icon: 'Award' },
    vendor_selected: { label: 'Vendor Selected', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' },
    vendor_deselected: { label: 'Vendor Deselected', color: 'text-muted-foreground', bg: 'bg-muted', icon: 'XCircle' },
    master_data_created: { label: 'Data Created', color: 'text-success', bg: 'bg-success/10', icon: 'Plus' },
    master_data_updated: { label: 'Data Updated', color: 'text-warning', bg: 'bg-warning/10', icon: 'Pencil' },
    master_data_deleted: { label: 'Data Deleted', color: 'text-destructive', bg: 'bg-destructive/10', icon: 'Trash2' },
  };

  const handleRoleChange = async (targetUser, newRole) => {
    if (targetUser?.id === user?.id) return;
    setActionLoading(targetUser?.id);
    setSuccessMsg('');
    try {
      const oldRole = targetUser?.role;
      const { error: updateError } = await supabase?.rpc('set_user_role', {
        target_email: targetUser?.email,
        new_role: newRole,
      });
      if (updateError) throw updateError;
      setUsers(prev => prev?.map(u => u?.id === targetUser?.id ? { ...u, role: newRole } : u));
      setSuccessMsg(`${targetUser?.email} is now ${ROLE_CONFIG?.[newRole]?.label}`);
      setTimeout(() => setSuccessMsg(''), 3500);
      // Audit log
      logAuditEvent({
        user,
        userProfile,
        action: AUDIT_ACTIONS?.USER_ROLE_CHANGED,
        entityType: AUDIT_ENTITIES?.USER,
        entityId: targetUser?.id,
        entityName: targetUser?.email,
        oldValue: { role: oldRole },
        newValue: { role: newRole },
      });
    } catch (err) {
      setError(err?.message || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleActive = async (targetUser) => {
    if (targetUser?.id === user?.id) return;
    setActionLoading(`active-${targetUser?.id}`);
    setSuccessMsg('');
    try {
      const newActive = !targetUser?.is_active;
      const { error: updateError } = await supabase?.from('user_profiles')?.update({ is_active: newActive, updated_at: new Date()?.toISOString() })?.eq('id', targetUser?.id);
      if (updateError) throw updateError;
      setUsers(prev => prev?.map(u => u?.id === targetUser?.id ? { ...u, is_active: newActive } : u));
      setSuccessMsg(`${targetUser?.email} has been ${newActive ? 'activated' : 'deactivated'}`);
      setTimeout(() => setSuccessMsg(''), 3500);
      // Audit log
      logAuditEvent({
        user,
        userProfile,
        action: newActive ? AUDIT_ACTIONS?.USER_ACTIVATED : AUDIT_ACTIONS?.USER_DEACTIVATED,
        entityType: AUDIT_ENTITIES?.USER,
        entityId: targetUser?.id,
        entityName: targetUser?.email,
        newValue: { is_active: newActive },
      });
    } catch (err) {
      setError(err?.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddAdminByEmail = async (e) => {
    e?.preventDefault();
    if (!adminEmailInput?.trim()) return;
    setAddAdminLoading(true);
    setAddAdminError('');
    setSuccessMsg('');
    try {
      const { data, error: rpcError } = await supabase?.rpc('set_admin_by_email', {
        target_email: adminEmailInput?.trim()?.toLowerCase()
      });
      if (rpcError) throw rpcError;
      if (!data) {
        setAddAdminError('No user found with that email address.');
      } else {
        setSuccessMsg(`${adminEmailInput?.trim()} has been granted Admin access.`);
        // Audit log
        logAuditEvent({
          user,
          userProfile,
          action: AUDIT_ACTIONS?.USER_ADDED_AS_ADMIN,
          entityType: AUDIT_ENTITIES?.USER,
          entityName: adminEmailInput?.trim(),
          newValue: { role: 'admin' },
        });
        setAdminEmailInput('');
        setTimeout(() => setSuccessMsg(''), 4000);
        await fetchUsers();
      }
    } catch (err) {
      setAddAdminError(err?.message || 'Failed to grant admin access');
    } finally {
      setAddAdminLoading(false);
    }
  };

  const filteredUsers = users?.filter(u =>
    !userSearch ||
    u?.email?.toLowerCase()?.includes(userSearch?.toLowerCase()) ||
    u?.full_name?.toLowerCase()?.includes(userSearch?.toLowerCase())
  );

  const adminCount = users?.filter(u => u?.role === 'admin')?.length;
  const estimatorCount = users?.filter(u => u?.role === 'estimator')?.length;
  const viewerCount = users?.filter(u => u?.role === 'viewer')?.length;
  const activeCount = users?.filter(u => u?.is_active)?.length;

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
            <Icon name="ShieldCheck" size={24} color="white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Admin Panel</h1>
            <p className="text-sm text-muted-foreground">Manage users, roles, and access control</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Users" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{users?.length}</p>
              <p className="text-xs text-muted-foreground">Total Users</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="ShieldCheck" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{adminCount}</p>
              <p className="text-xs text-muted-foreground">Admins</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Briefcase" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{estimatorCount}</p>
              <p className="text-xs text-muted-foreground">Estimators</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Icon name="UserCheck" size={20} color="#22c55e" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{activeCount}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          {[
            { id: 'users', label: 'User Management', icon: 'Users' },
            { id: 'matrix', label: 'Permissions Matrix', icon: 'Table' },
            { id: 'grant', label: 'Grant Admin by Email', icon: 'ShieldPlus' },
            { id: 'audit', label: 'Audit Log', icon: 'ClipboardList' },
          ]?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === tab?.id ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Icon name={tab?.icon} size={14} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Success / Error Messages */}
        {successMsg && (
          <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 text-green-600 rounded-xl px-4 py-3 mb-4 text-sm">
            <Icon name="CheckCircle" size={16} />
            {successMsg}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/30 text-destructive rounded-xl px-4 py-3 mb-4 text-sm">
            <Icon name="AlertCircle" size={16} />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><Icon name="X" size={14} /></button>
          </div>
        )}

        {/* Tab: User Management */}
        {activeTab === 'users' && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border gap-4">
              <h2 className="font-semibold text-foreground whitespace-nowrap">All Users</h2>
              <div className="relative flex-1 max-w-xs">
                <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e?.target?.value)}
                  placeholder="Search users..."
                  className="w-full h-9 pl-8 pr-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                />
              </div>
              <button onClick={fetchUsers} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                <Icon name="RefreshCw" size={14} />
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredUsers?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Icon name="Users" size={40} />
                <p className="mt-3 text-sm">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-6 py-3 text-muted-foreground font-medium">User</th>
                      <th className="text-left px-6 py-3 text-muted-foreground font-medium">Role</th>
                      <th className="text-left px-6 py-3 text-muted-foreground font-medium">Status</th>
                      <th className="text-left px-6 py-3 text-muted-foreground font-medium">Joined</th>
                      <th className="text-right px-6 py-3 text-muted-foreground font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers?.map((u) => {
                      const roleConf = ROLE_CONFIG?.[u?.role] || ROLE_CONFIG?.viewer;
                      return (
                        <tr key={u?.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-xs font-bold">
                                {(u?.full_name || u?.email || '?')?.charAt(0)?.toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium text-foreground">{u?.full_name || u?.display_name || '—'}</p>
                                <p className="text-xs text-muted-foreground">{u?.email}</p>
                              </div>
                              {u?.id === user?.id && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">You</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${roleConf?.bg} ${roleConf?.color}`}>
                              <Icon name={roleConf?.icon} size={11} />
                              {roleConf?.label}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${u?.is_active ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-500'}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${u?.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                              {u?.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-muted-foreground text-xs">
                            {u?.created_at ? new Date(u?.created_at)?.toLocaleDateString() : '—'}
                          </td>
                          <td className="px-6 py-4">
                            {u?.id !== user?.id ? (
                              <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                {/* Role buttons */}
                                {Object.entries(ROLE_CONFIG)?.map(([roleKey, conf]) => (
                                  <button
                                    key={roleKey}
                                    onClick={() => handleRoleChange(u, roleKey)}
                                    disabled={u?.role === roleKey || actionLoading === u?.id}
                                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                      u?.role === roleKey
                                        ? `${conf?.bg} ${conf?.color} cursor-default ring-1 ring-current/30`
                                        : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground'
                                    } disabled:opacity-50`}
                                    title={conf?.description}
                                  >
                                    {actionLoading === u?.id && u?.role !== roleKey ? (
                                      <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <Icon name={conf?.icon} size={11} />
                                    )}
                                    {conf?.label}
                                  </button>
                                ))}
                                {/* Active toggle */}
                                <button
                                  onClick={() => handleToggleActive(u)}
                                  disabled={actionLoading === `active-${u?.id}`}
                                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${u?.is_active ? 'bg-red-500/10 hover:bg-red-500/20 text-red-500' : 'bg-green-500/10 hover:bg-green-500/20 text-green-600'} disabled:opacity-50`}
                                >
                                  {actionLoading === `active-${u?.id}` ? (
                                    <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <Icon name={u?.is_active ? 'UserX' : 'UserCheck'} size={11} />
                                  )}
                                  {u?.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground text-right block pr-2">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab: Permissions Matrix */}
        {activeTab === 'matrix' && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Permissions Matrix</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Overview of what each role can access in the system</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-6 py-3 text-muted-foreground font-medium">Feature</th>
                    {Object.entries(ROLE_CONFIG)?.map(([roleKey, conf]) => (
                      <th key={roleKey} className="text-center px-6 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${conf?.bg} ${conf?.color}`}>
                          <Icon name={conf?.icon} size={11} />
                          {conf?.label}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PERMISSIONS_MATRIX?.map((row, idx) => (
                    <tr key={idx} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                      <td className="px-6 py-3.5 text-foreground font-medium">{row?.feature}</td>
                      <td className="px-6 py-3.5 text-center">
                        <Icon name={row?.admin ? 'CheckCircle2' : 'XCircle'} size={18} color={row?.admin ? 'var(--color-success)' : 'var(--color-muted-foreground)'} className="mx-auto" />
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <Icon name={row?.estimator ? 'CheckCircle2' : 'XCircle'} size={18} color={row?.estimator ? 'var(--color-success)' : 'var(--color-muted-foreground)'} className="mx-auto" />
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <Icon name={row?.viewer ? 'CheckCircle2' : 'XCircle'} size={18} color={row?.viewer ? 'var(--color-success)' : 'var(--color-muted-foreground)'} className="mx-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab: Grant Admin by Email */}
        {activeTab === 'grant' && (
          <div className="bg-card border border-border rounded-xl p-6 max-w-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="ShieldPlus" size={20} color="var(--color-accent)" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Grant Admin Access</h2>
                <p className="text-xs text-muted-foreground">Promote a registered user to Admin by their email</p>
              </div>
            </div>

            <form onSubmit={handleAddAdminByEmail} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">User Email</label>
                <input
                  type="email"
                  value={adminEmailInput}
                  onChange={(e) => setAdminEmailInput(e?.target?.value)}
                  placeholder="user@example.com"
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                  required
                />
              </div>

              {addAdminError && (
                <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                  <Icon name="AlertCircle" size={14} />
                  {addAdminError}
                </div>
              )}

              <button
                type="submit"
                disabled={addAdminLoading || !adminEmailInput?.trim()}
                className="w-full flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-primary to-accent text-white text-sm font-medium transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addAdminLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icon name="ShieldCheck" size={16} />
                )}
                Grant Admin Access
              </button>
            </form>

            <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border/50">
              <p className="text-xs text-muted-foreground flex items-start gap-2">
                <Icon name="Info" size={14} className="mt-0.5 shrink-0" />
                The user must already have a registered account. Admin access grants full control over user management and site settings.
              </p>
            </div>
          </div>
        )}

        {/* Tab: Audit Log */}
        {activeTab === 'audit' && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border gap-4">
              <div>
                <h2 className="font-semibold text-foreground">System Audit Log</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Immutable record of all critical system actions</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={auditFilter}
                    onChange={e => setAuditFilter(e?.target?.value)}
                    placeholder="Filter logs..."
                    className="w-52 h-9 pl-8 pr-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                </div>
                <button onClick={fetchAuditLogs} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
                  <Icon name="RefreshCw" size={14} />
                  Refresh
                </button>
              </div>
            </div>

            {auditError && (
              <div className="flex items-center gap-2 bg-destructive/10 border-b border-destructive/20 text-destructive px-6 py-3 text-sm">
                <Icon name="AlertCircle" size={14} />
                {auditError}
              </div>
            )}

            {auditLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredAuditLogs?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Icon name="ClipboardList" size={40} />
                <p className="mt-3 text-sm">No audit log entries found</p>
                <p className="text-xs mt-1">Actions will appear here as users interact with the system</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-5 py-3 text-muted-foreground font-medium">Timestamp</th>
                      <th className="text-left px-5 py-3 text-muted-foreground font-medium">User</th>
                      <th className="text-left px-5 py-3 text-muted-foreground font-medium">Action</th>
                      <th className="text-left px-5 py-3 text-muted-foreground font-medium">Entity</th>
                      <th className="text-left px-5 py-3 text-muted-foreground font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAuditLogs?.map((log) => {
                      const actionConf = ACTION_LABELS?.[log?.action] || { label: log?.action, color: 'text-foreground', bg: 'bg-muted', icon: 'Activity' };
                      return (
                        <tr key={log?.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                          <td className="px-5 py-3.5 whitespace-nowrap">
                            <p className="text-xs text-foreground font-medium">
                              {log?.created_at ? new Date(log?.created_at)?.toLocaleDateString() : '—'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {log?.created_at ? new Date(log?.created_at)?.toLocaleTimeString() : ''}
                            </p>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="text-xs font-medium text-foreground truncate max-w-[160px]">{log?.user_email || '—'}</p>
                            {log?.user_role && (
                              <span className="text-xs text-muted-foreground capitalize">{log?.user_role}</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${actionConf?.bg} ${actionConf?.color}`}>
                              <Icon name={actionConf?.icon} size={11} />
                              {actionConf?.label}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="text-xs font-medium text-foreground capitalize">{log?.entity_type || '—'}</p>
                            {log?.entity_name && (
                              <p className="text-xs text-muted-foreground truncate max-w-[140px]">{log?.entity_name}</p>
                            )}
                          </td>
                          <td className="px-5 py-3.5 max-w-[200px]">
                            {log?.old_value && log?.new_value ? (
                              <div className="text-xs space-y-0.5">
                                {Object.keys(log?.new_value || {})?.slice(0, 2)?.map(k => (
                                  <div key={k} className="flex items-center gap-1.5">
                                    <span className="text-muted-foreground capitalize">{k}:</span>
                                    {log?.old_value?.[k] !== undefined && (
                                      <span className="line-through text-destructive/70">{String(log?.old_value?.[k])}</span>
                                    )}
                                    <Icon name="ArrowRight" size={10} className="text-muted-foreground" />
                                    <span className="text-success font-medium">{String(log?.new_value?.[k])}</span>
                                  </div>
                                ))}
                              </div>
                            ) : log?.new_value ? (
                              <div className="text-xs space-y-0.5">
                                {Object.keys(log?.new_value || {})?.slice(0, 2)?.map(k => (
                                  <div key={k} className="flex items-center gap-1">
                                    <span className="text-muted-foreground capitalize">{k}:</span>
                                    <span className="text-foreground font-medium truncate">{String(log?.new_value?.[k])}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-6 py-3 border-t border-border bg-muted/20 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Showing {filteredAuditLogs?.length} of {auditLogs?.length} entries
              </p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Icon name="Lock" size={11} />
                Audit logs are immutable and cannot be modified
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
